import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {observer} from 'mobx-react-lite';
import I18n from 'app/i18n';
import {faqStore} from 'app/store/global';
import {getLinkPath} from 'app/utils/link_helper';
import type {FaqCategoryKey} from 'app/utils/knowledgeCenter/categories';
import {buildFaqPreviewText} from 'app/utils/knowledgeCenter/faqPreview';
import {faqSlugsMatch} from 'app/utils/knowledgeCenter/faqSlug';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';

interface Props {
    categoryKey: FaqCategoryKey
}

const MAX_CONTEXT_WIDGET_ARTICLES = 5;

const KnowledgeCenterContextWidget = observer(({categoryKey}: Props): React.ReactElement | null => {
    const [openSlug, setOpenSlug] = useState<string | null>(null);
    const [summaryFetchStarted, setSummaryFetchStarted] = useState(
        () => faqStore.summaryFaqs.length > 0 || faqStore.faqs.length > 0
    );

    useEffect(() => {
        faqStore.loadMeta().catch(() => null);
    }, []);

    useEffect(() => {
        if (faqStore.summaryFaqs.length > 0 || faqStore.faqs.length > 0) {
            return;
        }

        setSummaryFetchStarted(true);
        faqStore.loadSummary();
    }, []);

    const articles = (faqStore.faqsByCategory[categoryKey] ?? []).slice(0, MAX_CONTEXT_WIDGET_ARTICLES);

    const handleQuestionClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        const {slug} = event.currentTarget.dataset;

        if (!slug) {
            return;
        }

        const toggle = (): void => {
            setOpenSlug(current => current === slug ? null : slug);
        };

        if (openSlug === slug) {
            toggle();

            return;
        }

        const hasText = faqStore.faqs.some(faq => faqSlugsMatch(faq.name, slug) && faq.text);

        if (hasText) {
            toggle();

            return;
        }

        faqStore.loadData().then(toggle).catch(() => null);
    }, [openSlug]);

    if (!summaryFetchStarted || faqStore.summaryLoading && articles.length === 0) {
        return null;
    }

    if (articles.length === 0) {
        return null;
    }

    const openArticle = openSlug
        ? faqStore.faqs.find(faq => faqSlugsMatch(faq.name, openSlug))
        : null;
    const openPreview = openArticle?.text
        ? buildFaqPreviewText(openArticle.text)
        : '';

    return (
        <section
            className="knowledge-center-context-widget"
            aria-label={I18n.t(`knowledge_center.context_titles.${categoryKey}`)}
        >
            <div className="knowledge-center-context-widget__head">
                <h3 className="knowledge-center-context-widget__title">
                    <span className="knowledge-center-context-widget__title-icon" aria-hidden>
                        <SpriteIcon name="#svg-question" className="svg-default" size={{height: 18, width: 18}} />
                    </span>
                    {I18n.t(`knowledge_center.context_titles.${categoryKey}`)}
                </h3>
                <Link
                    to={getLinkPath(`/faq/category/${categoryKey}`)}
                    className="knowledge-center-context-widget__all-link"
                >
                    {I18n.t('knowledge_center.all_articles')}
                </Link>
            </div>
            {articles.map(article => {
                const isOpen = openSlug === article.name;

                return (
                    <div
                        key={article.name}
                        className={`knowledge-center-context-widget__item${isOpen ? ' knowledge-center-context-widget__item--open' : ''}`}
                    >
                        <button
                            type="button"
                            className="knowledge-center-context-widget__question"
                            data-slug={article.name}
                            aria-expanded={isOpen}
                            onClick={handleQuestionClick}
                        >
                            <span className="knowledge-center-context-widget__question-text">
                                {article.title}
                            </span>
                            <span className="knowledge-center-context-widget__chevron" aria-hidden/>
                        </button>
                        {isOpen && openPreview && <div className="knowledge-center-context-widget__answer">
                            {openPreview}
                            {' '}
                            <Link to={getLinkPath(`/faq/${article.name}`)}>
                                {I18n.t('knowledge_center.read_full_article')}
                            </Link>
                        </div>
                        }
                    </div>
                );
            })}
        </section>
    );
});

export default KnowledgeCenterContextWidget;
